// Training Program Builder — Biotensegrity Model
// Engine | Ignition | Bounce | Full-System Force Transfer

export type ProgramFocus = 'engine' | 'ignition' | 'bounce' | 'balanced';
export type ProgramDuration = 4 | 6 | 8 | 12;
export type TrainingFrequency = 2 | 3 | 4 | 5;
export type TrainingPillar = 'engine' | 'ignition' | 'bounce';
export type FascialFocus =
  | 'foot/ankle stiffness'
  | 'posterior chain' |'lateral sling' |'anterior oblique sling' |'posterior oblique sling' |'spiral line';
export type ExerciseIntent = 'tension' | 'stiffness' | 'recoil' | 'acceleration' | 'transfer';
export type PrimaryWeakness = 'engine' | 'ignition' | 'bounce';
export type ReadinessStatus = 'high' | 'moderate' | 'low';

export interface BiotensegrityTag {
  pillar: TrainingPillar;
  fascialFocus: FascialFocus;
  intent: ExerciseIntent;
}

export interface Exercise {
  id: string;
  name: string;
  sets: number;
  reps: string;
  rest: string;
  intensity: string;
  notes: string;
  intentCue?: string;
  biotensegrityFocus?: string;
  progressionNote?: string;
  tag?: BiotensegrityTag;
}

export interface WorkoutDay {
  id: string;
  day: string;
  label: string;
  focus: string;
  pillar: TrainingPillar | 'integrated';
  sessionStructure: string[];
  exercises: Exercise[];
}

export interface WeeklyProgram {
  weekNumber: number;
  theme: string;
  days: WorkoutDay[];
}

export interface ProgramBuilderInputs {
  frequency: TrainingFrequency;
  sport: string;
  position: string;
  trainingAge: string;
  primaryWeakness: PrimaryWeakness;
  availableEquipment: string[];
  sessionLength: string;
  currentEps: number;
  readiness: ReadinessStatus;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
}

export interface TrainingProgram {
  id: string;
  focus: ProgramFocus;
  title: string;
  rationale: string;
  duration: ProgramDuration;
  daysPerWeek: number;
  frequency: TrainingFrequency;
  weeklyLayout: string[];
  weeks: WeeklyProgram[];
  progressionModel: string[];
  retestRecommendation: string;
  inputs?: ProgramBuilderInputs;
}

// ─── BIOTENSEGRITY PRINCIPLES ────────────────────────────────────────────────
export const BIOTENSEGRITY_PRINCIPLES = [
  'Global tension before isolated strength',
  'Foot/ankle stiffness before speed',
  'Fascial sling loading before rotational output',
  'Elastic recoil before max effort volume',
  'Movement quality before fatigue',
  'Force transfer through the whole system',
];

// ─── SESSION STRUCTURE TEMPLATE ──────────────────────────────────────────────
export const SESSION_STRUCTURE = [
  '1. Readiness Check',
  '2. Biotensegrity Movement Prep',
  '3. Sprint or Plyometric Primer',
  '4. Main Strength/Power Block',
  '5. Sprint/Jump/Med Ball Transfer Block',
  '6. Accessory Support',
  '7. Cooldown/Reset',
];

// ─── SPRINT EXERCISE LIBRARY ─────────────────────────────────────────────────
export const SPRINT_LIBRARY: Array<{ name: string; tag: BiotensegrityTag; defaultSets: number; defaultReps: string; defaultRest: string; intentCue: string }> = [
  {
    name: 'Wall Drive ISO',
    tag: { pillar: 'ignition', fascialFocus: 'posterior chain', intent: 'tension' },
    defaultSets: 3, defaultReps: '3×8s each', defaultRest: '90 sec',
    intentCue: 'Max force into wall. Lock position. Feel posterior chain tension.',
  },
  {
    name: 'Marching Wall Drive',
    tag: { pillar: 'ignition', fascialFocus: 'anterior oblique sling', intent: 'acceleration' },
    defaultSets: 3, defaultReps: '10 each', defaultRest: '90 sec',
    intentCue: 'Drive knee to 90°. Maintain 45° body angle. Rhythmic and powerful.',
  },
  {
    name: 'Falling Start',
    tag: { pillar: 'ignition', fascialFocus: 'anterior oblique sling', intent: 'acceleration' },
    defaultSets: 4, defaultReps: '1×10 yards', defaultRest: '2 min',
    intentCue: 'Fall forward past balance point. First step is a catch. Drive hard.',
  },
  {
    name: 'Push-Up Start',
    tag: { pillar: 'ignition', fascialFocus: 'posterior oblique sling', intent: 'acceleration' },
    defaultSets: 4, defaultReps: '1×10 yards', defaultRest: '2 min',
    intentCue: 'Explode from push-up position. Horizontal force application.',
  },
  {
    name: '10-Yard Acceleration',
    tag: { pillar: 'ignition', fascialFocus: 'posterior chain', intent: 'acceleration' },
    defaultSets: 6, defaultReps: '1', defaultRest: '2 min',
    intentCue: 'Max intent. Drive phase only. Record time every rep.',
  },
  {
    name: 'Sled Push / Sled March',
    tag: { pillar: 'engine', fascialFocus: 'posterior chain', intent: 'tension' },
    defaultSets: 4, defaultReps: '20 yards', defaultRest: '2.5 min',
    intentCue: 'Push through the ground. Short powerful steps. Full hip extension.',
  },
  {
    name: 'Hill Sprint',
    tag: { pillar: 'engine', fascialFocus: 'posterior chain', intent: 'acceleration' },
    defaultSets: 5, defaultReps: '1×30 yards', defaultRest: '2.5 min',
    intentCue: 'Drive into the hill. Lean forward. High knee drive.',
  },
  {
    name: 'Wicket Run',
    tag: { pillar: 'bounce', fascialFocus: 'foot/ankle stiffness', intent: 'stiffness' },
    defaultSets: 4, defaultReps: '1 run', defaultRest: '2 min',
    intentCue: 'Quick ground contacts. Maintain stride cycle. Ankle stiff.',
  },
  {
    name: 'Flying 10',
    tag: { pillar: 'ignition', fascialFocus: 'posterior chain', intent: 'transfer' },
    defaultSets: 4, defaultReps: '1', defaultRest: '3 min',
    intentCue: 'Build to max velocity. Measure the 10-yard fly section only.',
  },
  {
    name: 'Build-Up Sprint',
    tag: { pillar: 'ignition', fascialFocus: 'spiral line', intent: 'transfer' },
    defaultSets: 3, defaultReps: '1×40 yards', defaultRest: '3 min',
    intentCue: 'Gradual acceleration to 95%. Smooth mechanics throughout.',
  },
  {
    name: 'Lateral Crossover Sprint',
    tag: { pillar: 'bounce', fascialFocus: 'lateral sling', intent: 'transfer' },
    defaultSets: 4, defaultReps: '1×10 yards each', defaultRest: '90 sec',
    intentCue: 'Crossover step pattern. Drive laterally. Maintain upright posture.',
  },
  {
    name: 'Curved Sprint',
    tag: { pillar: 'ignition', fascialFocus: 'spiral line', intent: 'transfer' },
    defaultSets: 4, defaultReps: '1×20 yards', defaultRest: '2 min',
    intentCue: 'Lean into curve. Inside arm drives. Maintain speed through arc.',
  },
];

// ─── PLYOMETRIC EXERCISE LIBRARY ─────────────────────────────────────────────
export const PLYO_LIBRARY: Array<{ name: string; tag: BiotensegrityTag; defaultSets: number; defaultReps: string; defaultRest: string; intentCue: string }> = [
  {
    name: 'Pogo Jumps',
    tag: { pillar: 'bounce', fascialFocus: 'foot/ankle stiffness', intent: 'stiffness' },
    defaultSets: 4, defaultReps: '15', defaultRest: '2 min',
    intentCue: 'Minimal knee bend. Max ankle stiffness. Rapid ground contact.',
  },
  {
    name: 'Ankling Contacts',
    tag: { pillar: 'bounce', fascialFocus: 'foot/ankle stiffness', intent: 'stiffness' },
    defaultSets: 3, defaultReps: '20m', defaultRest: '90 sec',
    intentCue: 'Rapid dorsiflexion/plantarflexion cycle. No knee bend. Ground < 120ms.',
  },
  {
    name: 'Snap Downs',
    tag: { pillar: 'bounce', fascialFocus: 'posterior chain', intent: 'recoil' },
    defaultSets: 3, defaultReps: '6', defaultRest: '2 min',
    intentCue: 'Snap hips down rapidly. Absorb and rebound. Stiff landing.',
  },
  {
    name: 'Vertical Jump',
    tag: { pillar: 'ignition', fascialFocus: 'posterior chain', intent: 'recoil' },
    defaultSets: 4, defaultReps: '4', defaultRest: '90 sec',
    intentCue: 'Max arm swing. Full triple extension. Measure height.',
  },
  {
    name: 'Broad Jump',
    tag: { pillar: 'ignition', fascialFocus: 'posterior chain', intent: 'transfer' },
    defaultSets: 4, defaultReps: '4', defaultRest: '90 sec',
    intentCue: 'Max horizontal distance. Stick landing 2s. Measure each rep.',
  },
  {
    name: 'Triple Jump',
    tag: { pillar: 'bounce', fascialFocus: 'posterior chain', intent: 'transfer' },
    defaultSets: 3, defaultReps: '3', defaultRest: '2 min',
    intentCue: 'Hop-step-jump. Minimize ground contact. Measure total distance.',
  },
  {
    name: 'Lateral Bound',
    tag: { pillar: 'bounce', fascialFocus: 'lateral sling', intent: 'transfer' },
    defaultSets: 3, defaultReps: '5 each', defaultRest: '90 sec',
    intentCue: 'Max lateral displacement. Stick each landing. Drive off outside foot.',
  },
  {
    name: 'Skater Bound',
    tag: { pillar: 'bounce', fascialFocus: 'lateral sling', intent: 'transfer' },
    defaultSets: 3, defaultReps: '6 each', defaultRest: '2 min',
    intentCue: 'Continuous lateral bounds. Reach arm across. Controlled landing.',
  },
  {
    name: 'Split-Stance Bound',
    tag: { pillar: 'ignition', fascialFocus: 'anterior oblique sling', intent: 'acceleration' },
    defaultSets: 3, defaultReps: '5 each', defaultRest: '90 sec',
    intentCue: 'Drive from split stance. Explosive hip extension. Forward projection.',
  },
  {
    name: 'Single-Leg Pogos',
    tag: { pillar: 'bounce', fascialFocus: 'foot/ankle stiffness', intent: 'stiffness' },
    defaultSets: 3, defaultReps: '10 each', defaultRest: '2 min',
    intentCue: 'Single-leg ankle stiffness. Minimal knee bend. Rapid rebound.',
  },
  {
    name: 'Depth Drop',
    tag: { pillar: 'bounce', fascialFocus: 'posterior chain', intent: 'stiffness' },
    defaultSets: 4, defaultReps: '4', defaultRest: '2.5 min',
    intentCue: 'Step off box. Absorb with stiff ankles. No rebound — just absorb.',
  },
  {
    name: 'Depth Jump',
    tag: { pillar: 'bounce', fascialFocus: 'posterior chain', intent: 'recoil' },
    defaultSets: 4, defaultReps: '4', defaultRest: '3 min',
    intentCue: 'Step off. Minimal contact time. Explode up immediately. Measure RSI.',
  },
  {
    name: 'Hurdle Hop',
    tag: { pillar: 'bounce', fascialFocus: 'foot/ankle stiffness', intent: 'stiffness' },
    defaultSets: 4, defaultReps: '2 runs (6 hurdles)', defaultRest: '2.5 min',
    intentCue: 'Continuous. Minimal contact. Max height over each hurdle.',
  },
  {
    name: 'Rotational Med Ball Scoop',
    tag: { pillar: 'ignition', fascialFocus: 'posterior oblique sling', intent: 'transfer' },
    defaultSets: 3, defaultReps: '6 each', defaultRest: '90 sec',
    intentCue: 'Load hip. Rotate through. Release at hip height. Max velocity.',
  },
  {
    name: 'Rebound Broad Jump',
    tag: { pillar: 'bounce', fascialFocus: 'posterior chain', intent: 'recoil' },
    defaultSets: 3, defaultReps: '4 consecutive', defaultRest: '2 min',
    intentCue: 'Land and immediately rebound. Minimal ground contact. Continuous.',
  },
];

// ─── MOVEMENT PREP BLOCK ─────────────────────────────────────────────────────
const MOVEMENT_PREP: Exercise[] = [
  { id: 'prep-1', name: 'Hip 90/90 Rotations', sets: 1, reps: '10 each', rest: '—', intensity: 'Bodyweight', notes: 'Global tension activation. Slow and controlled.' },
  { id: 'prep-2', name: 'Foot/Ankle Circles + Toe Spread', sets: 1, reps: '15 each', rest: '—', intensity: 'Bodyweight', notes: 'Foot/ankle stiffness prep. Feel the ground.' },
  { id: 'prep-3', name: 'Lateral Band Walk', sets: 1, reps: '15 each', rest: '—', intensity: 'Light band', notes: 'Lateral sling activation. Glute med engagement.' },
  { id: 'prep-4', name: 'Inchworm + Reach', sets: 1, reps: '8', rest: '—', intensity: 'Bodyweight', notes: 'Fascial line lengthening. Posterior chain.' },
  { id: 'prep-5', name: 'A-Skip (movement quality)', sets: 1, reps: '20m', rest: '—', intensity: 'Bodyweight', notes: 'Sprint mechanics primer. Rhythm and posture.' },
];

// ─── COOLDOWN BLOCK ───────────────────────────────────────────────────────────
const COOLDOWN: Exercise[] = [
  { id: 'cool-1', name: 'Standing Quad Stretch', sets: 1, reps: '30s each', rest: '—', intensity: 'Bodyweight', notes: 'Anterior chain reset.' },
  { id: 'cool-2', name: 'Seated Hamstring Stretch', sets: 1, reps: '30s each', rest: '—', intensity: 'Bodyweight', notes: 'Posterior chain release.' },
  { id: 'cool-3', name: 'Calf/Achilles Stretch', sets: 1, reps: '30s each', rest: '—', intensity: 'Bodyweight', notes: 'Foot/ankle stiffness reset.' },
  { id: 'cool-4', name: 'Diaphragmatic Breathing', sets: 1, reps: '10 breaths', rest: '—', intensity: 'Bodyweight', notes: 'Nervous system downregulation.' },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function sprintEx(libIdx: number, overrides?: Partial<Exercise>): Exercise {
  const s = SPRINT_LIBRARY[libIdx];
  return {
    id: `sprint-${libIdx}`,
    name: s.name,
    sets: s.defaultSets,
    reps: s.defaultReps,
    rest: s.defaultRest,
    intensity: 'Max intent',
    notes: s.intentCue,
    intentCue: s.intentCue,
    biotensegrityFocus: `${s.tag.pillar} | ${s.tag.fascialFocus} | ${s.tag.intent}`,
    tag: s.tag,
    ...overrides,
  };
}

function plyoEx(libIdx: number, overrides?: Partial<Exercise>): Exercise {
  const p = PLYO_LIBRARY[libIdx];
  return {
    id: `plyo-${libIdx}`,
    name: p.name,
    sets: p.defaultSets,
    reps: p.defaultReps,
    rest: p.defaultRest,
    intensity: 'Bodyweight',
    notes: p.intentCue,
    intentCue: p.intentCue,
    biotensegrityFocus: `${p.tag.pillar} | ${p.tag.fascialFocus} | ${p.tag.intent}`,
    tag: p.tag,
    ...overrides,
  };
}

function strengthEx(id: string, name: string, sets: number, reps: string, rest: string, intensity: string, notes: string, pillar: TrainingPillar = 'engine'): Exercise {
  return { id, name, sets, reps, rest, intensity, notes, biotensegrityFocus: `${pillar} | posterior chain | tension` };
}

// ─── AUTO-ADJUSTMENT ─────────────────────────────────────────────────────────
function applyReadinessAdjustment(exercises: Exercise[], readiness: ReadinessStatus): Exercise[] {
  if (readiness === 'high') return exercises;
  return exercises.map(ex => {
    const name = ex.name.toLowerCase();
    if (readiness === 'low') {
      // Replace depth jumps with pogos
      if (name.includes('depth jump')) return { ...plyoEx(0), id: ex.id };
      // Replace max velocity with technical acceleration
      if (name.includes('flying 10') || name.includes('build-up sprint')) return { ...sprintEx(0), id: ex.id }; // Wall Drive ISO
      // Reduce sprint volume
      if (ex.tag?.intent === 'acceleration') return { ...ex, sets: Math.max(2, ex.sets - 2) };
      // Reduce plyo contacts
      if (ex.tag?.intent === 'recoil' || ex.tag?.intent === 'stiffness') return { ...ex, sets: Math.max(2, ex.sets - 1) };
    }
    if (readiness === 'moderate') {
      if (ex.tag?.intent === 'acceleration') return { ...ex, sets: Math.max(3, ex.sets - 1) };
    }
    return ex;
  });
}

function applyWeaknessAdjustment(exercises: Exercise[], weakness: PrimaryWeakness): Exercise[] {
  // Already handled by frequency-specific day design, but add extra emphasis
  return exercises;
}

// ─── 2 DAYS/WEEK ─────────────────────────────────────────────────────────────
function build2DayWeeks(inputs: ProgramBuilderInputs, duration: ProgramDuration): WeeklyProgram[] {
  const { readiness, primaryWeakness } = inputs;

  const day1Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(0), // Wall Drive ISO
    sprintEx(2), // Falling Start
    plyoEx(0),   // Pogo Jumps
    plyoEx(3),   // Vertical Jump
    strengthEx('s1', 'Trap Bar Deadlift', 4, '3–5', '3 min', '80–87% 1RM', 'Posterior chain force production. Reset each rep.'),
    strengthEx('s2', 'Back Squat', 3, '5', '2.5 min', '75–82% 1RM', 'Full depth. Controlled descent.'),
    strengthEx('s3', 'Romanian Deadlift', 3, '8', '2 min', '65–70% 1RM', 'Eccentric 4s. Fascial sling loading.'),
    sprintEx(4), // 10-Yard Acceleration
    plyoEx(4),   // Broad Jump
    ...COOLDOWN,
  ];

  const day2Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(7), // Wicket Run
    sprintEx(8), // Flying 10
    plyoEx(1),   // Ankling Contacts
    plyoEx(6),   // Lateral Bound
    strengthEx('s4', 'Bulgarian Split Squat', 3, '6 each', '2.5 min', '65–72% 1RM', 'Unilateral. Eccentric 3s.', 'ignition'),
    strengthEx('s5', 'Bench Press', 3, '5', '2.5 min', '78–85% 1RM', 'Leg drive engaged. Pause 1s at chest.', 'engine'),
    strengthEx('s6', 'Barbell Row', 3, '6', '2 min', '72–78% 1RM', 'Horizontal pull. Control eccentric.', 'engine'),
    plyoEx(11),  // Depth Jump
    sprintEx(9), // Build-Up Sprint
    ...COOLDOWN,
  ];

  const day1 = applyReadinessAdjustment(day1Exercises, readiness);
  const day2 = applyReadinessAdjustment(day2Exercises, readiness);

  const baseWeek: WorkoutDay[] = [
    {
      id: 'day-1', day: 'Monday', label: 'Full Body — Sprint + Strength',
      focus: 'Posterior Chain | Acceleration | Elastic Recoil',
      pillar: 'engine',
      sessionStructure: SESSION_STRUCTURE,
      exercises: day1,
    },
    {
      id: 'day-2', day: 'Thursday', label: 'Full Body — Speed + Power',
      focus: 'Lateral Sling | Max Velocity | Transfer',
      pillar: 'ignition',
      sessionStructure: SESSION_STRUCTURE,
      exercises: day2,
    },
  ];

  return Array.from({ length: duration }, (_, i) => ({
    weekNumber: i + 1,
    theme: i < 2 ? 'Foundation' : i < 4 ? 'Accumulation' : i < 6 ? 'Intensification' : i === duration - 1 ? 'Deload' : 'Maintenance',
    days: baseWeek,
  }));
}

// ─── 3 DAYS/WEEK ─────────────────────────────────────────────────────────────
function build3DayWeeks(inputs: ProgramBuilderInputs, duration: ProgramDuration): WeeklyProgram[] {
  const { readiness, primaryWeakness } = inputs;

  const day1Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(2), // Falling Start
    sprintEx(4), // 10-Yard Acceleration
    plyoEx(0),   // Pogo Jumps
    strengthEx('s1', 'Trap Bar Deadlift', 5, '3–5', '3–4 min', '85–92% 1RM', 'Max force production. Reset each rep.'),
    strengthEx('s2', 'Back Squat', 4, '4–6', '3 min', '80–87% 1RM', 'Full depth. Controlled descent.'),
    strengthEx('s3', 'Romanian Deadlift', 3, '6–8', '2.5 min', '70–75% 1RM', 'Eccentric 4s.'),
    strengthEx('s4', 'Nordic Hamstring Curl', 3, '5', '2 min', 'Bodyweight', 'Eccentric only — catch at bottom.'),
    sprintEx(5), // Sled Push
    ...COOLDOWN,
  ];

  const day2Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(0), // Wall Drive ISO
    sprintEx(1), // Marching Wall Drive
    plyoEx(3),   // Vertical Jump
    plyoEx(4),   // Broad Jump
    strengthEx('s5', 'Hang Power Clean', 5, '3', '3 min', '70–80% 1RM', 'Max bar velocity. Triple extension.', 'ignition'),
    strengthEx('s6', 'Jump Squat', 4, '5', '2.5 min', '30–40% 1RM', 'Maximal intent. Land softly.', 'ignition'),
    plyoEx(13),  // Rotational Med Ball Scoop
    sprintEx(8), // Flying 10
    ...COOLDOWN,
  ];

  const day3Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    plyoEx(1),   // Ankling Contacts
    plyoEx(2),   // Snap Downs
    plyoEx(12),  // Hurdle Hop
    plyoEx(10),  // Depth Drop
    plyoEx(11),  // Depth Jump
    strengthEx('s7', 'Bulgarian Split Squat', 3, '6 each', '2.5 min', '65–72% 1RM', 'Unilateral. Eccentric 3s.', 'bounce'),
    plyoEx(9),   // Single-Leg Pogos
    plyoEx(14),  // Rebound Broad Jump
    sprintEx(7), // Wicket Run
    ...COOLDOWN,
  ];

  const d1 = applyReadinessAdjustment(day1Exercises, readiness);
  const d2 = applyReadinessAdjustment(day2Exercises, readiness);
  const d3 = applyReadinessAdjustment(day3Exercises, readiness);

  const baseWeek: WorkoutDay[] = [
    {
      id: 'day-1', day: 'Monday', label: 'Strength Focus — Engine',
      focus: 'Posterior Chain | Force Production | Acceleration',
      pillar: 'engine',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d1,
    },
    {
      id: 'day-2', day: 'Wednesday', label: 'Acceleration Focus — Ignition',
      focus: 'Explosive Power | Sprint Starts | RFD',
      pillar: 'ignition',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d2,
    },
    {
      id: 'day-3', day: 'Friday', label: 'Elastic/Reactive Focus — Bounce',
      focus: 'Ankle Stiffness | RSI | Elastic Recoil',
      pillar: 'bounce',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d3,
    },
  ];

  return Array.from({ length: duration }, (_, i) => ({
    weekNumber: i + 1,
    theme: i < 2 ? 'Foundation' : i < 4 ? 'Accumulation' : i < 6 ? 'Intensification' : i === duration - 1 ? 'Deload' : 'Maintenance',
    days: baseWeek,
  }));
}

// ─── 4 DAYS/WEEK ─────────────────────────────────────────────────────────────
function build4DayWeeks(inputs: ProgramBuilderInputs, duration: ProgramDuration): WeeklyProgram[] {
  const { readiness } = inputs;

  const day1Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(5), // Sled Push
    sprintEx(6), // Hill Sprint
    strengthEx('s1', 'Trap Bar Deadlift', 5, '3–5', '3–4 min', '85–92% 1RM', 'Max force. Reset each rep.'),
    strengthEx('s2', 'Back Squat', 4, '4–6', '3 min', '80–87% 1RM', 'Full depth.'),
    strengthEx('s3', 'Romanian Deadlift', 3, '6–8', '2.5 min', '70–75% 1RM', 'Eccentric 4s.'),
    strengthEx('s4', 'Isometric Squat Hold', 3, '3×6s', '90 sec', 'Max effort', 'Push into pins at 90°.'),
    strengthEx('s5', 'Nordic Hamstring Curl', 3, '5', '2 min', 'Bodyweight', 'Eccentric only.'),
    ...COOLDOWN,
  ];

  const day2Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(0), // Wall Drive ISO
    sprintEx(2), // Falling Start
    sprintEx(3), // Push-Up Start
    sprintEx(4, { sets: 6 }), // 10-Yard Acceleration
    strengthEx('s6', 'Hang Power Clean', 5, '3', '3 min', '70–80% 1RM', 'Max bar velocity.', 'ignition'),
    strengthEx('s7', 'Jump Squat', 4, '5', '2.5 min', '30–40% 1RM', 'Maximal intent.', 'ignition'),
    sprintEx(8), // Flying 10
    sprintEx(9), // Build-Up Sprint
    ...COOLDOWN,
  ];

  const day3Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    plyoEx(0),   // Pogo Jumps
    plyoEx(1),   // Ankling Contacts
    plyoEx(2),   // Snap Downs
    plyoEx(9),   // Single-Leg Pogos
    plyoEx(12),  // Hurdle Hop
    plyoEx(10),  // Depth Drop
    plyoEx(11),  // Depth Jump
    plyoEx(14),  // Rebound Broad Jump
    sprintEx(7), // Wicket Run
    ...COOLDOWN,
  ];

  const day4Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(10), // Lateral Crossover Sprint
    sprintEx(11), // Curved Sprint
    plyoEx(6),    // Lateral Bound
    plyoEx(7),    // Skater Bound
    plyoEx(13),   // Rotational Med Ball Scoop
    strengthEx('s8', 'Trap Bar Deadlift (speed)', 4, '3', '2.5 min', '60–65% 1RM', 'Max bar speed intent.', 'ignition'),
    strengthEx('s9', 'Broad Jump → Sprint 10', 3, '3', '2 min', 'Max effort', 'Jump then immediately sprint. Transfer block.', 'ignition'),
    plyoEx(4),    // Broad Jump
    plyoEx(3),    // Vertical Jump
    ...COOLDOWN,
  ];

  const d1 = applyReadinessAdjustment(day1Exercises, readiness);
  const d2 = applyReadinessAdjustment(day2Exercises, readiness);
  const d3 = applyReadinessAdjustment(day3Exercises, readiness);
  const d4 = applyReadinessAdjustment(day4Exercises, readiness);

  const baseWeek: WorkoutDay[] = [
    {
      id: 'day-1', day: 'Monday', label: 'Day 1 — Engine',
      focus: 'Max Strength | Posterior Chain | Force Production',
      pillar: 'engine',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d1,
    },
    {
      id: 'day-2', day: 'Tuesday', label: 'Day 2 — Ignition',
      focus: 'Acceleration | Sprint Starts | RFD',
      pillar: 'ignition',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d2,
    },
    {
      id: 'day-3', day: 'Thursday', label: 'Day 3 — Bounce',
      focus: 'Ankle Stiffness | Elastic Recoil | RSI',
      pillar: 'bounce',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d3,
    },
    {
      id: 'day-4', day: 'Friday', label: 'Day 4 — Integrated Transfer',
      focus: 'Lateral Power | Rotational Transfer | Full System',
      pillar: 'integrated',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d4,
    },
  ];

  return Array.from({ length: duration }, (_, i) => ({
    weekNumber: i + 1,
    theme: i < 2 ? 'Foundation' : i < 4 ? 'Accumulation' : i < 6 ? 'Intensification' : i === duration - 1 ? 'Deload' : 'Maintenance',
    days: baseWeek,
  }));
}

// ─── 5 DAYS/WEEK ─────────────────────────────────────────────────────────────
function build5DayWeeks(inputs: ProgramBuilderInputs, duration: ProgramDuration): WeeklyProgram[] {
  const { readiness } = inputs;

  // 5-day: lower daily volume, higher weekly exposure
  const day1Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(5, { sets: 3 }), // Sled Push — reduced
    strengthEx('s1', 'Trap Bar Deadlift', 4, '3–4', '3 min', '82–88% 1RM', 'Force production. Lower volume.'),
    strengthEx('s2', 'Romanian Deadlift', 3, '6', '2.5 min', '68–72% 1RM', 'Eccentric 4s.'),
    plyoEx(0, { sets: 3 }), // Pogo Jumps
    ...COOLDOWN,
  ];

  const day2Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(0), // Wall Drive ISO
    sprintEx(2, { sets: 3 }), // Falling Start
    sprintEx(4, { sets: 4 }), // 10-Yard Acceleration
    strengthEx('s3', 'Hang Power Clean', 4, '3', '2.5 min', '68–75% 1RM', 'Max bar velocity.', 'ignition'),
    plyoEx(3, { sets: 3 }), // Vertical Jump
    ...COOLDOWN,
  ];

  const day3Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    plyoEx(1),   // Ankling Contacts
    plyoEx(2),   // Snap Downs
    plyoEx(12, { sets: 3 }), // Hurdle Hop
    strengthEx('s4', 'Bulgarian Split Squat', 3, '5 each', '2.5 min', '62–68% 1RM', 'Unilateral. Eccentric 3s.', 'bounce'),
    plyoEx(10, { sets: 3 }),  // Depth Drop
    ...COOLDOWN,
  ];

  const day4Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(8, { sets: 3 }), // Flying 10
    sprintEx(9, { sets: 3 }), // Build-Up Sprint
    plyoEx(4, { sets: 3 }), // Broad Jump
    plyoEx(13, { sets: 3 }), // Rotational Med Ball Scoop
    strengthEx('s5', 'Jump Squat', 3, '4', '2.5 min', '30–35% 1RM', 'Maximal intent. Lower volume.', 'ignition'),
    ...COOLDOWN,
  ];

  const day5Exercises: Exercise[] = [
    ...MOVEMENT_PREP,
    sprintEx(10, { sets: 3 }), // Lateral Crossover Sprint
    plyoEx(6, { sets: 3 }),    // Lateral Bound
    plyoEx(7, { sets: 3 }),    // Skater Bound
    strengthEx('s6', 'Trap Bar Deadlift (speed)', 3, '3', '2.5 min', '58–62% 1RM', 'Max bar speed. Low volume.', 'ignition'),
    plyoEx(14, { sets: 2 }),   // Rebound Broad Jump — reduced for 5-day
    ...COOLDOWN,
  ];

  const d1 = applyReadinessAdjustment(day1Exercises, readiness);
  const d2 = applyReadinessAdjustment(day2Exercises, readiness);
  const d3 = applyReadinessAdjustment(day3Exercises, readiness);
  const d4 = applyReadinessAdjustment(day4Exercises, readiness);
  const d5 = applyReadinessAdjustment(day5Exercises, readiness);

  const baseWeek: WorkoutDay[] = [
    {
      id: 'day-1', day: 'Monday', label: 'Engine — Strength Base',
      focus: 'Max Strength | Posterior Chain | Low Volume',
      pillar: 'engine',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d1,
    },
    {
      id: 'day-2', day: 'Tuesday', label: 'Ignition — Acceleration',
      focus: 'Acceleration | Sprint Starts | RFD',
      pillar: 'ignition',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d2,
    },
    {
      id: 'day-3', day: 'Wednesday', label: 'Bounce — Elastic/Reactive',
      focus: 'Ankle Stiffness | Elastic Recoil | Recovery Day',
      pillar: 'bounce',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d3,
    },
    {
      id: 'day-4', day: 'Thursday', label: 'Ignition — Speed Transfer',
      focus: 'Max Velocity | Power Transfer | Med Ball',
      pillar: 'ignition',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d4,
    },
    {
      id: 'day-5', day: 'Friday', label: 'Integrated — Lateral + Transfer',
      focus: 'Lateral Power | Full System Transfer | Low Volume',
      pillar: 'integrated',
      sessionStructure: SESSION_STRUCTURE,
      exercises: d5,
    },
  ];

  return Array.from({ length: duration }, (_, i) => ({
    weekNumber: i + 1,
    theme: i < 2 ? 'Foundation' : i < 4 ? 'Accumulation' : i < 6 ? 'Intensification' : i === duration - 1 ? 'Deload' : 'Maintenance',
    days: baseWeek,
  }));
}

// ─── WEEKLY LAYOUT DESCRIPTIONS ──────────────────────────────────────────────
function getWeeklyLayout(frequency: TrainingFrequency): string[] {
  const layouts: Record<TrainingFrequency, string[]> = {
    2: [
      'Day 1 (Mon): Full Body — Sprint + Strength (Engine + Ignition)',
      'Day 2 (Thu): Full Body — Speed + Power (Bounce + Transfer)',
      'Notes: Full-body sessions. Higher exercise density. Sprint + jump both days. Best for in-season or low availability.',
    ],
    3: [
      'Day 1 (Mon): Strength Focus — Engine (Force Production + Acceleration)',
      'Day 2 (Wed): Acceleration Focus — Ignition (Explosive Power + Sprint Starts)',
      'Day 3 (Fri): Elastic/Reactive Focus — Bounce (Ankle Stiffness + RSI)',
      'Notes: Balanced strength/speed/power split. One emphasis per day.',
    ],
    4: [
      'Day 1 (Mon): Engine — Max Strength + Posterior Chain',
      'Day 2 (Tue): Ignition — Acceleration + Sprint Starts',
      'Day 3 (Thu): Bounce — Elastic Recoil + RSI',
      'Day 4 (Fri): Integrated Transfer — Lateral Power + Full System',
      'Notes: Separate emphasis days. Full biotensegrity model coverage.',
    ],
    5: [
      'Day 1 (Mon): Engine — Strength Base (Lower Volume)',
      'Day 2 (Tue): Ignition — Acceleration',
      'Day 3 (Wed): Bounce — Elastic/Reactive (Recovery Emphasis)',
      'Day 4 (Thu): Ignition — Speed Transfer',
      'Day 5 (Fri): Integrated — Lateral + Transfer (Low Volume)',
      'Notes: Advanced model. Lower daily volume. Higher weekly exposure. Readiness-adjusted.',
    ],
  };
  return layouts[frequency];
}

// ─── PROGRAM RATIONALE ───────────────────────────────────────────────────────
function buildRationale(inputs: ProgramBuilderInputs): string {
  const { primaryWeakness, frequency, readiness, currentEps, sport, position } = inputs;
  const weaknessMap = {
    engine: 'Engine (max strength/force production)',
    ignition: 'Ignition (rate of force development/explosiveness)',
    bounce: 'Bounce (elastic stiffness/reactive power)',
  };
  const readinessNote = readiness === 'low' ?' Readiness is low — sprint volume reduced, depth jumps replaced with pogos, max velocity replaced with technical acceleration.'
    : readiness === 'moderate' ?' Readiness is moderate — sprint volume slightly reduced.' :'';
  return `${sport ? `${sport}${position ? ` (${position})` : ''} athlete. ` : ''}Current EPS: ${currentEps}. Primary weakness: ${weaknessMap[primaryWeakness]}. ${frequency}-day/week program following the Biotensegrity model: global tension before isolated strength, foot/ankle stiffness before speed, fascial sling loading before rotational output, elastic recoil before max effort volume.${readinessNote}`;
}

// ─── MAIN BUILDER ─────────────────────────────────────────────────────────────
export function buildProgramFromInputs(inputs: ProgramBuilderInputs, duration: ProgramDuration): TrainingProgram {
  const { frequency, primaryWeakness, readiness } = inputs;
  const id = `prog-${primaryWeakness}-${frequency}d-${duration}wk`;

  const focusMap: Record<PrimaryWeakness, ProgramFocus> = {
    engine: 'engine',
    ignition: 'ignition',
    bounce: 'bounce',
  };

  const titleMap: Record<TrainingFrequency, string> = {
    2: `${frequency}-Day Full Body Power Program`,
    3: `${frequency}-Day Strength/Speed/Power Split`,
    4: `${frequency}-Day Emphasis Day Program`,
    5: `${frequency}-Day Advanced Athlete Model`,
  };

  const progressionModel = [
    'Weeks 1–2: Foundation — movement quality, biotensegrity prep, low-amplitude elastic work',
    'Weeks 3–4: Accumulation — volume increase, sprint/jump exposure, strength base',
    'Weeks 5–6: Intensification — reduced volume, higher intensity, max intent sprint/jump',
    'Week 7: Deload — 50% volume reduction, maintain movement quality',
    'Week 8+: Retest — full EPS battery, compare Engine/Ignition/Bounce scores',
  ];

  const weekBuilders: Record<TrainingFrequency, (i: ProgramBuilderInputs, d: ProgramDuration) => WeeklyProgram[]> = {
    2: build2DayWeeks,
    3: build3DayWeeks,
    4: build4DayWeeks,
    5: build5DayWeeks,
  };

  return {
    id,
    focus: focusMap[primaryWeakness],
    title: titleMap[frequency],
    rationale: buildRationale(inputs),
    duration,
    daysPerWeek: frequency,
    frequency,
    weeklyLayout: getWeeklyLayout(frequency),
    weeks: weekBuilders[frequency](inputs, duration),
    progressionModel,
    retestRecommendation: `Retest full EPS battery after ${duration} weeks. Compare Engine, Ignition, and Bounce scores. Adjust program focus based on updated primary weakness.`,
    inputs,
  };
}

// ─── LEGACY BUILDER (backward compat) ────────────────────────────────────────
export function buildProgram(focus: ProgramFocus, duration: ProgramDuration): TrainingProgram {
  const weaknessMap: Record<ProgramFocus, PrimaryWeakness> = {
    engine: 'engine', ignition: 'ignition', bounce: 'bounce', balanced: 'ignition',
  };
  const defaultInputs: ProgramBuilderInputs = {
    frequency: 4,
    sport: '',
    position: '',
    trainingAge: 'intermediate',
    primaryWeakness: weaknessMap[focus],
    availableEquipment: ['barbell', 'dumbbells', 'sled', 'med ball'],
    sessionLength: '60-75 min',
    currentEps: 50,
    readiness: 'high',
    engineScore: 50,
    ignitionScore: 50,
    bounceScore: 50,
  };
  return buildProgramFromInputs(defaultInputs, duration);
}